import { Typography, Button } from '@lucid/lego';
import type { ModuleFileReference } from '../../../../../../api/services/lupp/order/dto.order';
import { escapeHtml, formatDateForLanguage } from '../../../../../../utils/tools';
import styles from './utils.module.scss';

export type { ModuleFileReference };

export type ProFormaHistoryRow = {
  orderDocumentIdentifier: string;
  orderMasterIdentifier: string;
  sfdcFileIdentifier: string;
  status: string;
  documentCreatedAt: string;
  canDownload: boolean;
  fileNameOriginal: string;
};

type CreateColumnsParams = {
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  onDownloadClick: (sfdcFileId: string, fileName: string) => void;
  /** Whether a download is in progress */
  isDownloading?: boolean;
  /** sfdcFileIdentifier of the document currently being downloaded */
  downloadingDocumentId?: string | null;
  /** Handler to open PDF in a new tab when file name is clicked */
  onFileNameClick?: (sfdcFileId: string, fileName: string) => void;
};

/**
 * Maps document status to display label
 * @param status - Document status
 * @param labels - Localized labels
 * @returns Display label for the status
 */
export const getStatusLabel = (status: string, labels: Record<string, string>): string => {
  const normalizedStatus = status.trim().toUpperCase();
  const generatingLabel = (labels?.['proforma.generating'] || 'GENERATING').toUpperCase();
  const generatedLabel = (labels?.['proforma.generated'] || 'GENERATED').toUpperCase();
  const sentLabel = (labels?.['proforma.sent'] || 'SENT').toUpperCase();

  const statusMap: Record<string, string> = {
    PROCESSING: generatingLabel,
    GENERATING: generatingLabel,
    GENERATED: generatedLabel,
    SENT: sentLabel,
  };

  return statusMap[normalizedStatus] || normalizedStatus;
};

/**
 * Creates table columns for the Pro-Forma history table
 * @param params - Column configuration parameters
 * @returns Array of column definitions
 */
export const createColumns = ({
  theme,
  labels,
  onDownloadClick,
  isDownloading,
  downloadingDocumentId,
  onFileNameClick,
}: CreateColumnsParams) => {
  const fileNameLabel = labels?.['proforma.fileName'] || 'Doc#';
  const statusLabel = labels?.['proforma.status'] || 'Status';
  const createdDateLabel = labels?.['proforma.createdDate'] || 'Generated Date';
  const actionLabel = labels?.['proforma.action'] || 'Action';
  const downloadLabel = labels?.['proforma.download'] || 'Download';
  const downloadingLabel = labels?.['proforma.downloading'] || 'Downloading...';
  const proformaInvoiceLabel = labels?.['proforma.invoice'] || 'Pro-Forma Invoice';

  return [
    {
      key: 'fileNameOriginal',
      title: fileNameLabel,
      width: '30%',
      sortable: false,
      render: (_: unknown, record: ProFormaHistoryRow) => {
        const isClickable =
          record.canDownload &&
          record.sfdcFileIdentifier &&
          record.sfdcFileIdentifier !== '-' &&
          onFileNameClick;

        const handleClick = () => {
          if (isClickable) {
            onFileNameClick(record.sfdcFileIdentifier, record.sfdcFileIdentifier);
          }
        };

        return (
          <Typography
            variant="body"
            size="medium"
            theme={theme}
            color={isClickable ? 'link' : 'black'}
            onClick={isClickable ? handleClick : undefined}
          >
            {escapeHtml(record.fileNameOriginal) || proformaInvoiceLabel}
          </Typography>
        );
      },
    },
    {
      key: 'status',
      title: statusLabel,
      width: '30%',
      sortable: false,
      render: (_: unknown, record: ProFormaHistoryRow) => (
        <Typography variant="body" size="medium" theme={theme} color="black">
          {record.status === 'GENERATED'
            ? labels?.['proforma.generated'] || 'Generated'
            : labels?.['proforma.generating'] || 'Generating'}
        </Typography>
      ),
    },
    {
      key: 'documentCreatedAt',
      title: createdDateLabel,
      width: '25%',
      sortable: false,
      render: (_: unknown, record: ProFormaHistoryRow) => (
        <Typography variant="body" size="medium" theme={theme} color="black">
          {record.documentCreatedAt}
        </Typography>
      ),
    },
    {
      key: 'action',
      title: actionLabel,
      width: '15%',
      sortable: false,
      render: (_: unknown, record: ProFormaHistoryRow) => {
        const isCurrentDownloading =
          isDownloading && downloadingDocumentId === record.sfdcFileIdentifier;

        return record.canDownload ? (
          <Button
            variant="button"
            type="secondary"
            color="neutral"
            size="small"
            theme={theme === 'dark' ? 'light' : 'dark'}
            label={isCurrentDownloading ? downloadingLabel : downloadLabel}
            handleClick={() =>
              onDownloadClick(record.sfdcFileIdentifier, record.sfdcFileIdentifier)
            }
            disabled={isCurrentDownloading}
            gaComponent="orderhub-download-proforma-invoice"
            dataTestId={`download-proforma-invoice-${record.sfdcFileIdentifier}`}
            className={styles.downloadButton}
          />
        ) : null;
      },
    },
  ];
};

/**
 * Formats a date string to a localized display format
 * @param dateString - ISO date string or null
 * @param locale - User's locale (e.g., 'nl-nl', 'en-us')
 * @returns Formatted date string or '-' (EN: "Sep 29, 2026", NL: "29 sep 2026")
 */
export const formatProFormaDate = (
  dateString: string | null | undefined,
  locale?: string
): string => {
  return formatDateForLanguage(dateString, locale);
};

type CreateHistoryDataParams = {
  invoices: ModuleFileReference[];
  labels: Record<string, string>;
  locale?: string;
};

/**
 * Transforms pre-filtered pro-forma invoice file references to table rows
 * @param params - Data creation parameters
 * @returns Array of history rows
 */
export const createHistoryData = ({
  invoices,
  labels,
  locale,
}: CreateHistoryDataParams): ProFormaHistoryRow[] => {
  if (!invoices || invoices.length === 0) {
    return [];
  }

  return invoices.map((file) => ({
    orderDocumentIdentifier: file.partnerPortalDocumentIdentifier || '',
    orderMasterIdentifier: file.moduleReferenceIdentifier || '',
    sfdcFileIdentifier: file.sfdcFileIdentifier || '-',
    fileNameOriginal: file.fileNameOriginal || '',
    status: getStatusLabel(file.fileGenerationStatus ?? '', labels),
    documentCreatedAt: formatProFormaDate(file.createdDatetime, locale),
    canDownload: !!file.sfdcFileIdentifier && file.sfdcFileIdentifier !== '-',
  }));
};
